import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { scanFridge } from '../../services/api';
import toast from 'react-hot-toast';
import { useCart } from '../../context/CartContext';

const FridgeProducts = () => {
  const { fridgeId } = useParams();
  const navigate = useNavigate();
  const [fridge, setFridge] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart, cart, getCartTotal } = useCart();

  useEffect(() => {
    loadFridgeProducts();
  }, [fridgeId]);

  const loadFridgeProducts = async () => {
    try {
      const response = await scanFridge(fridgeId);
      if (response.data.success) {
        setFridge(response.data.fridge);
        setProducts(response.data.products);
      }
    } catch (error) {
      toast.error('Failed to load fridge products');
      navigate('/scan');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart({ ...product, fridgeId });
    toast.success(`${product.name} added to cart`);
  };

  const handleCheckout = () => {
    navigate('/fridge-checkout', { 
      state: { 
        fridgeId,
        fridgeName: fridge?.location,
        apartment: fridge?.apartment
      } 
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto px-4 py-8">
        {/* Fridge Info */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">
            {fridge?.apartment} - {fridge?.location}
          </h1>
          <p className="text-gray-600 mt-2">Scan your items and pay instantly</p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => (
            <div key={product.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-800">{product.name}</h3>
                <p className="text-gray-600 text-sm mt-1">{product.description}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-2xl font-bold text-blue-600">
                    ₹{product.price}
                  </span>
                  <span className="text-sm text-gray-500">
                    Stock: {product.quantity}
                  </span>
                </div>
                {product.expiry_date && (
                  <p className="text-xs text-red-500 mt-2">
                    Expires: {new Date(product.expiry_date).toLocaleDateString()}
                  </p>
                )}
                <button
                  onClick={() => handleAddToCart(product)}
                  disabled={product.quantity === 0}
                  className={`mt-4 w-full py-2 px-4 rounded-lg font-medium
                    ${product.quantity > 0 
                      ? 'bg-blue-600 text-white hover:bg-blue-700' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                >
                  {product.quantity > 0 ? 'Add to Cart' : 'Out of Stock'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Cart Summary */}
        {cart.length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t p-4">
            <div className="container mx-auto flex justify-between items-center">
              <div>
                <span className="text-gray-600">Items: {cart.length}</span>
                <span className="text-xl font-bold text-blue-600 ml-4">
                  Total: ₹{getCartTotal()}
                </span>
              </div>
              <button
                onClick={handleCheckout}
                className="bg-green-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-green-700"
              >
                Checkout
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FridgeProducts;